import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { getBrowserSupabase } from '@/lib/browserSupabase';
import { queryClient } from '@/lib/queryClient';
import { getBrowserPreferredLanguage } from '@/lib/i18n';

// ============================================================================
// TYPES (State Machine Architecture)
// ============================================================================

export type PlanType = 'starter' | 'pro' | 'business' | null;
export type SubscriptionStatusType = 'pending' | 'trial' | 'active' | 'cancelled' | 'expired';

export interface ProfileData {
  subscriptionStatus: SubscriptionStatusType;
  subscriptionPlan: PlanType;
  emailVerified: boolean;
  firstName: string | null;
  lastName: string | null;
  businessName: string | null;
}

export interface EstablishmentData {
  id: string;
  name: string;
  googlePlaceId: string | null;
  googleReviewsLink: string | null;
  logoUrl: string | null;
  city: string | null;
}

// ============================================================================
// CONTEXT TYPE (State Machine)
// ============================================================================

export interface AppStateContextType {
  // Auth (Core)
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isGoogleUser: boolean;

  // ⭐ STATE MACHINE - 4 Key States
  isEmailVerified: boolean;
  hasActiveSubscription: boolean;
  isConfigured: boolean;
  isOnboardingComplete: boolean;
  onboardingStep: string | null;

  // Profile Data
  subscriptionStatus: SubscriptionStatusType | null;
  subscriptionPlan: PlanType;
  firstName: string | null;
  lastName: string | null;
  businessName: string | null;

  // Establishments
  establishments: EstablishmentData[];
  currentEstablishment: EstablishmentData | null;

  // Loading States
  isInitializing: boolean;
  isLoadingProfile: boolean;
  isLoadingEstablishments: boolean;

  // Error States
  profileError: string | null;
  establishmentError: string | null;

  // Actions
  signOut: () => Promise<void>;
  refreshState: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshEstablishments: () => Promise<void>;
  setCurrentEstablishment: (id: string) => void;
  clearErrors: () => void;
  forceCleanSession: () => Promise<void>;
}

// ============================================================================
// CONTEXT
// ============================================================================

const AppStateContext = createContext<AppStateContextType | undefined>(undefined);

// ============================================================================
// HELPER: Clean localStorage & Session Storage
// ============================================================================

const cleanAllStorage = () => {
  if (typeof window === 'undefined') return;

  console.log('[AppState] 🧹 Cleaning all storage...');

  window.localStorage.clear();
  window.sessionStorage.clear();

  const keysToRemove = Object.keys(window.localStorage).filter((key) =>
    key.startsWith('sb-') || key.includes('supabase') || key.includes('auth')
  );
  keysToRemove.forEach((key) => window.localStorage.removeItem(key));
};

// ============================================================================
// PROVIDER (State Machine Architecture)
// ============================================================================

export const AppStateProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusType | null>(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState<PlanType>(null);
  const [profileEmailVerified, setProfileEmailVerified] = useState(false);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<string | null>(null);
  const [onboardingStep, setOnboardingStep] = useState<string | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileNotFound, setProfileNotFound] = useState(false);

  const [establishments, setEstablishments] = useState<EstablishmentData[]>([]);
  const [currentEstablishment, setCurrentEstablishmentState] = useState<EstablishmentData | null>(null);
  const [isLoadingEstablishments, setIsLoadingEstablishments] = useState(false);
  const [establishmentError, setEstablishmentError] = useState<string | null>(null);

  const initRef = useRef(false);
  const fetchingRef = useRef(false);
  const orphanCheckRef = useRef(false);
  const supabaseRef = useRef<any | null>(null);
  const userRef = useRef<User | null>(null);
  const subscriptionStatusRef = useRef<SubscriptionStatusType | null>(null);

  const getSupabase = useCallback(async () => {
    if (supabaseRef.current) return supabaseRef.current;
    const client = await getBrowserSupabase();
    supabaseRef.current = client;
    return client;
  }, []);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    subscriptionStatusRef.current = subscriptionStatus;
  }, [subscriptionStatus]);

  const isAuthenticated = !!user;
  const isGoogleUser = user?.app_metadata?.provider === 'google';
  const isEmailVerified = isGoogleUser || !!user?.email_confirmed_at || profileEmailVerified;
  const hasActiveSubscription = subscriptionStatus === 'active' || subscriptionStatus === 'trial';
  const isConfigured = establishments.length > 0;
  const isOnboardingComplete = onboardingStep === 'completed';

  const forceCleanSession = useCallback(async () => {
    console.log('[AppState] 🔥 Force cleaning session...');

    try {
      const supabase = await getSupabase();
      await supabase.auth.signOut({ scope: 'global' });
    } catch (err) {
      console.error('[AppState] ❌ SignOut error:', err);
    }

    cleanAllStorage();

    setUser(null);
    setSession(null);
    setSubscriptionStatus(null);
    setSubscriptionPlan(null);
    setProfileEmailVerified(false);
    setFirstName(null);
    setLastName(null);
    setBusinessName(null);
    setOnboardingStep(null);
    setEstablishments([]);
    setCurrentEstablishmentState(null);
    setProfileError(null);
    setEstablishmentError(null);
    setProfileNotFound(false);
  }, [getSupabase]);

  const fetchProfile = useCallback(async (userId: string): Promise<boolean> => {
    if (fetchingRef.current) return true;
    fetchingRef.current = true;

    console.log('[AppState] 📋 Fetching profile for:', userId);
    setIsLoadingProfile(true);
    setProfileError(null);
    setProfileNotFound(false);

    try {
      const supabase = await getSupabase();
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          subscription_status,
          subscription_plan,
          email_verified,
          first_name,
          last_name,
          business_name,
          onboarding_step
        `)
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('[AppState] ❌ Profile fetch error:', error);
        setProfileError('Impossible de charger le profil');
        fetchingRef.current = false;
        return false;
      }

      if (data) {
        console.log('[AppState] ✅ Profile loaded:', {
          status: data.subscription_status,
          plan: data.subscription_plan,
          emailVerified: data.email_verified,
        });

        setSubscriptionStatus((data.subscription_status as SubscriptionStatusType) || 'pending');
        setSubscriptionPlan((data.subscription_plan as PlanType) || null);
        setProfileEmailVerified(data.email_verified === true);
        setFirstName(data.first_name);
        setLastName(data.last_name);
        setBusinessName(data.business_name);
        setOnboardingStep(data.onboarding_step || 'pending');
        setProfileNotFound(false);
        fetchingRef.current = false;
        return true;
      }

      console.warn('[AppState] ⚠️ Profile missing, attempting auto-create:', userId);

      try {
        const { data: authData, error: authErr } = await supabase.auth.getUser();
        if (authErr) throw authErr;

        const authUser = authData.user;
        const meta = (authUser?.user_metadata ?? {}) as Record<string, any>;
        const isGoogleProvider =
          authUser?.app_metadata?.provider === 'google' ||
          (Array.isArray(authUser?.app_metadata?.providers) && authUser.app_metadata.providers.includes('google'));

        const payload = {
          user_id: userId,
          business_name: (meta.business_name as string) || 'À configurer',
          first_name: (meta.first_name as string) ?? null,
          last_name: (meta.last_name as string) ?? null,
          job_title: (meta.job_title as string) ?? null,
          language: ((meta.language as string) ?? getBrowserPreferredLanguage()),
          notification_email: authUser?.email ?? null,
          subscription_status: 'pending',
          subscription_plan: null,
          email_verified: isGoogleProvider,
        };

        const { error: createErr } = await supabase
          .from('profiles')
          .upsert(payload, { onConflict: 'user_id' });

        if (createErr) throw createErr;

        console.log('[AppState] ✅ Minimal profile created');

        setSubscriptionStatus('pending');
        setSubscriptionPlan(null);
        setProfileEmailVerified(payload.email_verified === true);
        setFirstName(payload.first_name);
        setLastName(payload.last_name);
        setBusinessName(payload.business_name);
        setOnboardingStep('pending');
        setProfileNotFound(false);
        fetchingRef.current = false;
        return true;
      } catch (createProfileErr) {
        console.error('[AppState] ❌ Failed to auto-create profile:', createProfileErr);
        setProfileNotFound(true);
        setProfileError('Impossible de créer le profil');
        fetchingRef.current = false;
        return false;
      }
    } catch (err) {
      console.error('[AppState] ❌ Profile fetch exception:', err);
      setProfileError('Erreur de connexion au serveur');
      fetchingRef.current = false;
      return false;
    } finally {
      setIsLoadingProfile(false);
    }
  }, [getSupabase]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!user || isLoadingProfile || !profileNotFound || orphanCheckRef.current) {
      return;
    }

    const path = window.location.pathname;
    const isInPaymentFlow =
      path.startsWith('/choose-plan') ||
      path.startsWith('/success') ||
      path.startsWith('/verify-email');

    if (isInPaymentFlow) {
      console.warn('[AppState] ⚠️ Profile missing during payment flow - skipping auto-clean');
      return;
    }

    orphanCheckRef.current = true;

    console.log('[AppState] 🚨 Auto-cleaning orphan session...');
    toast.error('Session invalide', {
      description: 'Votre session a expiré. Veuillez vous reconnecter.',
    });

    forceCleanSession().then(() => {
      window.location.href = '/login';
    });
  }, [user, isLoadingProfile, profileNotFound, forceCleanSession]);

  const fetchEstablishments = useCallback(async (userId: string) => {
    console.log('[AppState] 🏢 Checking establishment count for:', userId);
    setIsLoadingEstablishments(true);
    setEstablishmentError(null);

    try {
      const supabase = await getSupabase();
      const { count, error } = await supabase
        .from('establishments')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (error) {
        console.error('[AppState] ❌ Establishments count error:', error);
        setEstablishmentError('Impossible de charger les établissements');
        return;
      }

      const establishmentCount = count || 0;
      console.log('[AppState] ✅ Establishment count:', establishmentCount);

      if (establishmentCount > 0) {
        setEstablishments(Array(establishmentCount).fill({ id: 'placeholder' }) as any);
      } else {
        setEstablishments([]);
      }
      setCurrentEstablishmentState(null);
    } catch (err) {
      console.error('[AppState] ❌ Establishments count exception:', err);
      setEstablishmentError('Erreur de connexion');
    } finally {
      setIsLoadingEstablishments(false);
    }
  }, [getSupabase]);

  const signOut = useCallback(async () => {
    console.log('[AppState] 🚪 Signing out with full cleanup...');

    try {
      const supabase = await getSupabase();
      await supabase.auth.signOut({ scope: 'global' });
    } catch (err) {
      console.error('[AppState] ❌ SignOut error:', err);
    }

    cleanAllStorage();

    setUser(null);
    setSession(null);
    setSubscriptionStatus(null);
    setSubscriptionPlan(null);
    setProfileEmailVerified(false);
    setFirstName(null);
    setLastName(null);
    setBusinessName(null);
    setOnboardingStep(null);
    setEstablishments([]);
    setCurrentEstablishmentState(null);
    setProfileError(null);
    setEstablishmentError(null);
    setProfileNotFound(false);
  }, [getSupabase]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  }, [user, fetchProfile]);

  const refreshEstablishments = useCallback(async () => {
    if (user) {
      await fetchEstablishments(user.id);
    }
  }, [user, fetchEstablishments]);

  const refreshState = useCallback(async () => {
    console.log('[AppState] 🔄 Refreshing full state...');
    if (user) {
      await Promise.all([fetchProfile(user.id), fetchEstablishments(user.id)]);
    }
  }, [user, fetchProfile, fetchEstablishments]);

  const setCurrentEstablishment = useCallback((id: string) => {
    const found = establishments.find((e) => e.id === id);
    if (found) {
      setCurrentEstablishmentState(found);
    }
  }, [establishments]);

  const clearErrors = useCallback(() => {
    setProfileError(null);
    setEstablishmentError(null);
  }, []);

  useEffect(() => {
    if (initRef.current) return;
    if (typeof window === 'undefined') {
      setIsInitializing(false);
      return;
    }

    initRef.current = true;
    console.log('[AppState] 🚀 Initializing State Machine...');

    let isMounted = true;
    let unsubscribe = () => {};

    void (async () => {
      try {
        const supabase = await getSupabase();
        if (!isMounted) return;

        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
          console.log('[AppState] 🔔 Auth event:', event);

          if (event === 'INITIAL_SESSION') return;
          if (event === 'TOKEN_REFRESHED') {
            console.log('[AppState] 🔇 TOKEN_REFRESHED ignored (no re-render)');
            return;
          }

          const incomingUserId = currentSession?.user?.id;
          const currentUserId = userRef.current?.id;

          if (
            event === 'SIGNED_IN' &&
            incomingUserId &&
            incomingUserId === currentUserId &&
            subscriptionStatusRef.current !== null
          ) {
            console.log('[AppState] 🔇 SIGNED_IN ignored (same user already loaded)');
            return;
          }

          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          orphanCheckRef.current = false;

          if (event === 'SIGNED_IN' && currentSession?.user) {
            console.log('[AppState] ✅ User signed in, fetching data...');
            window.sessionStorage.setItem('ot_browser_session', '1');
            window.setTimeout(() => {
              void fetchProfile(currentSession.user.id);
              void fetchEstablishments(currentSession.user.id);
            }, 0);
          }

          if (event === 'SIGNED_OUT') {
            console.log('[AppState] 🚪 User signed out, clearing state and query cache...');
            queryClient.clear();
            setSubscriptionStatus(null);
            setSubscriptionPlan(null);
            setEstablishments([]);
            setCurrentEstablishmentState(null);
            setProfileNotFound(false);
          }

          setIsInitializing(false);
        });

        unsubscribe = () => subscription.unsubscribe();

        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!isMounted) return;

        console.log('[AppState] 📋 Initial session check:', currentSession ? 'Found' : 'None');

        if (currentSession?.user && !window.sessionStorage.getItem('ot_browser_session')) {
          console.log('[AppState] 🔒 Browser session expired (browser was closed). Forcing re-login...');
          await supabase.auth.signOut();
          cleanAllStorage();
          setSession(null);
          setUser(null);
          setIsInitializing(false);
          return;
        }

        if (currentSession?.user) {
          window.sessionStorage.setItem('ot_browser_session', '1');
        }

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          void fetchProfile(currentSession.user.id);
          void fetchEstablishments(currentSession.user.id);
        }

        setIsInitializing(false);
      } catch (error) {
        console.error('[AppState] ❌ Initialization error:', error);
        setIsInitializing(false);
      }
    })();

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [fetchProfile, fetchEstablishments, getSupabase]);

  useEffect(() => {
    if (!isInitializing) {
      console.log('[AppState] 🎯 State Machine Status:', {
        isAuthenticated,
        isEmailVerified,
        hasActiveSubscription,
        isConfigured,
        subscriptionStatus,
        establishmentCount: establishments.length,
        profileNotFound,
      });
    }
  }, [isAuthenticated, isEmailVerified, hasActiveSubscription, isConfigured, subscriptionStatus, establishments.length, isInitializing, profileNotFound]);

  const value: AppStateContextType = {
    user,
    session,
    isAuthenticated,
    isGoogleUser,
    isEmailVerified,
    hasActiveSubscription,
    isConfigured,
    isOnboardingComplete,
    onboardingStep,
    subscriptionStatus,
    subscriptionPlan,
    firstName,
    lastName,
    businessName,
    establishments,
    currentEstablishment,
    isInitializing,
    isLoadingProfile,
    isLoadingEstablishments,
    profileError,
    establishmentError,
    signOut,
    refreshState,
    refreshProfile,
    refreshEstablishments,
    setCurrentEstablishment,
    clearErrors,
    forceCleanSession,
  };

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
};

// ============================================================================
// HOOK
// ============================================================================

export const useAppState = () => {
  const context = useContext(AppStateContext);
  if (context === undefined) {
    throw new Error('useAppState must be used within an AppStateProvider');
  }
  return context;
};
